import { Form as FormikForm, Formik } from "formik";
import useFetch from "hooks/useFetch";
import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  ConfirmDialog,
  FormikTextarea,
  FormikTextField,
  Icon,
  PageHeader,
  SegmentedControl,
  useToast,
} from "stories/Admin";
import * as Yup from "yup";
import SectionTabs from "pages/AdminLayout/SectionTabs";
import { RoutePaths } from "enums/RoutePaths";
import styles from "../AdminPushPage/AdminPushPage.module.scss";

type Target = "user" | "token";

interface DirectPushForm {
  target: Target;
  user: string;
  token: string;
  title: string;
  body: string;
  imgurl: string;
}

interface DirectPushResult {
  id: number | null;
  status: "SENT" | "FAILED" | string;
  recipient: string;
  error: string | null;
}

const MAX_CHAR_LIMIT = 255;

const validationSchema = Yup.object().shape({
  target: Yup.string().oneOf(["user", "token"]),
  user: Yup.string().when("target", {
    is: "user",
    then: (s) => s.trim().required("Skriv Firebase-ID eller e-post"),
  }),
  token: Yup.string().when("target", {
    is: "token",
    then: (s) => s.trim().required("Lim inn FCM-tokenet"),
  }),
  title: Yup.string().required("Tittel er påkrevd"),
  body: Yup.string()
    .required("Melding er påkrevd")
    .max(MAX_CHAR_LIMIT, `Melding kan ikke overstige ${MAX_CHAR_LIMIT} tegn`),
  imgurl: Yup.string().url("Ugyldig URL").nullable(),
});

const recipientOf = (v: DirectPushForm) =>
  v.target === "token" ? `token …${v.token.trim().slice(-12)}` : v.user.trim();

const DirectPushPage: React.FC = () => {
  const { isLoading, error, execute } = useFetch<any>();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const [formValues, setFormValues] = useState<DirectPushForm | null>(null);
  const [failure, setFailure] = useState<DirectPushResult | null>(null);

  const initialUser = searchParams.get("bruker") ?? "";

  const handleConfirmSubmit = async () => {
    if (!formValues) return;
    setFailure(null);

    const payload =
      formValues.target === "token"
        ? { token: formValues.token.trim() }
        : { user: formValues.user.trim() };

    const response = (await execute(
      "/api/v1/notification/send-to-device",
      "POST",
      {
        ...payload,
        title: formValues.title,
        body: formValues.body,
        imgurl: formValues.imgurl || null,
      },
    )) as DirectPushResult | null;

    setFormValues(null);
    if (!response) return;
    if (response.status === "SENT") {
      toast.success(`Push sendt til ${response.recipient}`);
    } else {
      setFailure(response);
    }
  };

  return (
    <>
      <PageHeader
        title="Push-meldinger"
        subtitle="Send et varsel til én bestemt bruker eller enhet, f.eks. for å teste."
      />
      <SectionTabs />

      <Formik<DirectPushForm>
        initialValues={{
          target: "user",
          user: initialUser,
          token: "",
          title: "",
          body: "",
          imgurl: "",
        }}
        validationSchema={validationSchema}
        validateOnMount
        onSubmit={(values) => setFormValues(values)}
      >
        {({ isValid, values, setFieldValue }) => (
          <FormikForm className={styles.layout} noValidate>
            <Card title="Ny melding til én bruker">
              <SegmentedControl<Target>
                label="Mottaker"
                value={values.target}
                onChange={(v) => setFieldValue("target", v)}
                options={[
                  { value: "user", label: "Bruker" },
                  { value: "token", label: "FCM-token" },
                ]}
              />
              {values.target === "user" ? (
                <FormikTextField
                  name="user"
                  label="Firebase-ID eller e-post"
                  required
                  placeholder="f.eks. ola@example.com"
                  help="Sendes til push-tokenet som er lagret på brukeren (siste enhet hen logget inn på)."
                />
              ) : (
                <FormikTextarea
                  name="token"
                  label="FCM-token"
                  required
                  rows={3}
                  placeholder="Lim inn tokenet fra appen …"
                />
              )}
              <FormikTextField
                name="title"
                label="Tittel"
                required
                placeholder="Skriv tittel …"
              />
              <FormikTextarea
                name="body"
                label="Melding"
                required
                rows={3}
                placeholder="Skriv meldingen …"
                maxLength={MAX_CHAR_LIMIT}
                maxChars={MAX_CHAR_LIMIT}
                onChange={(e) =>
                  setFieldValue("body", e.target.value.slice(0, MAX_CHAR_LIMIT))
                }
              />
              <FormikTextField
                name="imgurl"
                label="Bildelenke (valgfri)"
                placeholder="https://…"
                help="Må være en gyldig URL."
              />
              {error && <Alert tone="error">Feilmelding: {error.text}</Alert>}
              {failure && (
                <Alert tone="error">
                  Push til {failure.recipient} feilet: {failure.error}. Den er
                  logget i <Link to={RoutePaths.PushLog}>Push-loggen</Link>.
                </Alert>
              )}
              <div className={styles.actions}>
                <Button
                  icon={<Icon name="bell" size={16} />}
                  onClick={() => setFormValues(values)}
                  loading={isLoading}
                  disabled={!isValid}
                >
                  Send push
                </Button>
              </div>
            </Card>

            <section className={styles.preview} aria-label="Forhåndsvisning">
              <span className={styles.previewLabel}>FORHÅNDSVISNING</span>
              <div className={styles.notification}>
                <span className={styles.appIcon}>
                  <Icon name="moon" size={18} />
                </span>
                <span className={styles.notificationText}>
                  <b>{values.title || "Tittel"}</b>
                  <span>{values.body || "Meldingen vises her."}</span>
                </span>
              </div>
            </section>
          </FormikForm>
        )}
      </Formik>

      <ConfirmDialog
        open={formValues !== null}
        title="Send push?"
        confirmLabel="Bekreft og send"
        busy={isLoading}
        onConfirm={handleConfirmSubmit}
        onCancel={() => setFormValues(null)}
      >
        «{formValues?.title}» sendes nå til{" "}
        <b>{formValues ? recipientOf(formValues) : ""}</b>.
      </ConfirmDialog>
    </>
  );
};

export default DirectPushPage;
